import { useCallback, useEffect, useRef, useState } from "react";
import { AxiosError } from "axios";
import { useErrorHandler } from "contexts/errorContext";
import { getCachedConfiguration, setCacheConfiguration } from "utils/functions/configurationUtil";
import { getDeviceInfo } from "utils/functions/deviceUtil";
import { getFeaturedBannerSelection, setFeaturedBannerSelection } from "utils/functions/bannerUtil";
import { getBannerOffers, getFeaturedOffers, getOffers, getTransactions } from "api/offers";
import { getPanelistConfiguration, setPanelistState } from "api/panelist";
import { Offer, OfferStatuses } from "interfaces/Offer";
import { PanelistConfig } from "interfaces/PanelistConfig";
import { PanelistState } from "interfaces/PanelistState";
import { Promotion } from "interfaces/Promotion";
import { OfferCurrencySale } from "interfaces/OfferCurrencySale";
import MyOfferCardItem from "components/offers/MyOfferCardItem";
import Modal from "../../components/ui/Modal";
import QrModal from "components/ui/QrModal";
import Spinner from "components/ui/Spinner";
import Alert from "components/ui/Alert";
import Booster from "components/ui/Booster";
import HeroOfferModalContent from "components/offers/OfferModal/HeroOfferModalContent";
import RewardModalContent from "components/rewards/RewardModal/RewardModalContent";
import OfferFilter from "components/offers/OfferFilter";
import HeroOfferCardItem from "components/offers/HeroOfferCardItem";
import OfferCarousel from "components/offers/OfferCarousel";
import OfferDeviceSelector from "components/offers/OfferDeviceSelector";
import MainFooter from "pages/SurveyWall/MainFooter/MainFooter";
import { MdWindow } from "react-icons/md";

import * as c from "./OffersPage.module.css";

function OffersPage() {
  const handleError = useErrorHandler();
  const { mobileOS } = getDeviceInfo();
  const [modalOpen, setModalOpen] = useState(false);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [qrCode, setQrCode] = useState("");
  const [offerUrl, setOfferUrl] = useState("");
  const [offers, setOffers] = useState<Offer[]>([]);
  const [featuredOffers, setFeaturedOffers] = useState<Offer[]>([]);
  const [bannerOffers, setBannerOffers] = useState<Offer[]>([]);
  const [bannerOffer, setBannerOffer] = useState<Offer | null>(null);
  const [myOffers, setMyOffers] = useState<any>([]);
  const [showEmptyMyOffers, setShowEmptyMyOffers] = useState(false);
  const [isCarouselEmpty, setIsCarouselEmpty] = useState(false);
  const [isInit, setIsInit] = useState(true);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isBannerOfferLoading, setIsBannerOfferLoading] = useState(true);
  const [isOfferLoading, setIsOfferLoading] = useState(true);
  const [isFeaturedOfferLoading, setIsFeaturedOfferLoading] = useState(true);
  const [isUpdatingOffers, setIsUpdatingOffers] = useState(false);
  const [activeFilters, setActiveFilters] = useState<any>([]);
  const [activeTab, setActiveTab] = useState("all");
  const [isThresholdReached, setIsThresholdReached] = useState(false);
  const [modalType, setModalType] = useState<"offer" | "reward" | null>(null);
  const [modalContent, setModalContent] = useState<{
    attemptedAt: string;
    attributionWindowMinutes?: number | null;
    standardGoals: Offer["standardGoals"];
    totalStandardGoalRewardIac?: number;
    purchaseGoals: Offer["purchaseGoals"];
    totalPurchaseGoalRewardIac?: number;
    promotion?: Promotion;
    campaignCurrencySale?: OfferCurrencySale;
    isCompleted?: boolean;
    description: string[];
    instructions?: string[];
    requirements?: string[];
    platforms: string[];
    operatingSystems: string[];
    title: string;
    rewardIac: number;
    thumbnailUrl: string;
    heroImageUrl?: string;
    offerId: number;
    offerStatus: OfferStatuses;
    isFeatured: boolean;
  }>({
    standardGoals: [],
    purchaseGoals: [],
    platforms: [],
    title: "",
    rewardIac: 0,
    offerId: 0,
    thumbnailUrl: "",
    offerStatus: OfferStatuses.Open,
    attemptedAt: "",
    description: [],
    operatingSystems: [],
    isFeatured: false,
  });
  const [showOfferOnboardingWidget, setShowOfferOnboardingWidget] = useState(false);
  const configuration = getCachedConfiguration();
  const activeTabRef = useRef(activeTab);
  const isFetchingRef = useRef(false);

  const getModifiedGoals = (offer: Offer) => {
    let foundCompleted = false;

    const modifiedStandardGoals = offer.standardGoals!.length
      ? offer.standardGoals!.map((goal, index) => {
          if (index === 0 && !goal.isCompleted) {
            return { ...goal, isPending: true };
          }

          if (foundCompleted && !goal.isCompleted) {
            foundCompleted = false;
            return { ...goal, isPending: true };
          }

          if (goal.isCompleted) {
            foundCompleted = true;
          }

          if (offer.standardGoals!.every((g) => g.isCompleted)) {
            return { ...goal, isPending: false };
          }

          return goal;
        })
      : [];

    return {
      ...offer,
      standardGoals: modifiedStandardGoals,
    };
  };

  const openModal = (offer: Offer, type: "offer" | "reward") => {
    document.body.style.overflow = "hidden";
    if (type === "reward") {
      offer = getModifiedGoals(offer);
    }

    const content =
      type === "offer"
        ? {
            attemptedAt: offer.attemptedAt,
            standardGoals: offer.standardGoals,
            totalStandardGoalRewardIac: offer.totalStandardGoalRewardIac,
            purchaseGoals: offer.purchaseGoals,
            totalPurchaseGoalRewardIac: offer.totalPurchaseGoalRewardIac,
            promotion: offer.promotion,
            campaignCurrencySale: offer.campaignCurrencySale,
            instructions: offer.instructions,
            description: offer.description,
            requirements: offer.requirements,
            platforms: offer.platforms,
            operatingSystems: offer.operatingSystems,
            title: offer.title,
            rewardIac: offer.rewardIac,
            thumbnailUrl: offer.thumbnailUrl,
            heroImageUrl: offer.heroImageUrl,
            offerId: offer.id,
            offerStatus: offer.status,
            isFeatured: offer.isFeatured,
          }
        : {
            attemptedAt: offer.attemptedAt,
            attributionWindowMinutes: offer.attributionWindowMinutes,
            standardGoals: offer.standardGoals,
            totalStandardGoalRewardIac: offer.totalStandardGoalRewardIac,
            purchaseGoals: offer.purchaseGoals,
            totalPurchaseGoalRewardIac: offer.totalPurchaseGoalRewardIac,
            promotion: offer.promotion,
            campaignCurrencySale: offer.campaignCurrencySale,
            isCompleted: offer.isCompleted,
            description: offer.description,
            requirements: offer.requirements,
            platforms: offer.platforms,
            operatingSystems: offer.operatingSystems,
            title: offer.title,
            rewardIac: offer.rewardIac,
            thumbnailUrl: offer.thumbnailUrl,
            heroImageUrl: offer.heroImageUrl,
            offerId: offer.id,
            offerStatus: offer.status,
          };

    setModalContent(content);

    setModalType(type);
    setModalOpen(true);
  };
  const closeModal = () => {
    document.body.style.overflow = "";
    setModalOpen(false);
    setModalType(null);
  };

  const closeQrModal = () => {
    document.body.style.overflow = "";
    setQrModalOpen(false);
    if (modalType) {
      setModalOpen(true);
    }
  };

  const openQrModal = (qrCode: string, url: string) => {
    setQrCode(qrCode);
    setOfferUrl(url);

    setModalOpen(false);
    setQrModalOpen(true);
  };

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
  };

  const LIMIT = configuration!.offersLimit ?? 30;

  const loadMyOffers = () => {
    setIsOfferLoading(true);
    getTransactions(configuration!.hashed_publisher_app_uid!)
      .then((response) => {
        setIsOfferLoading(false);
        setMyOffers(response);
        setShowEmptyMyOffers(true);
        setIsCarouselEmpty(response.length === 0);
      })
      .catch((error) => {
        handleError(error as AxiosError);
      });
  };

  const loadBannerOffers = useCallback(async () => {
    setIsBannerOfferLoading(true);

    try {
      const res = await getBannerOffers(
        configuration!.hashed_publisher_app_uid!,
        mobileOS,
        activeFilters,
      );

      const nextBannerOffers: Offer[] = Array.isArray(res)
        ? res
        : Array.isArray((res as any)?.data)
          ? (res as any).data
          : Array.isArray((res as any)?.offers)
            ? (res as any).offers
            : [];

      setBannerOffers(nextBannerOffers);
    } catch (error) {
      handleError(error as AxiosError);
      setBannerOffers([]);
    } finally {
      setIsBannerOfferLoading(false);
    }
  }, [activeFilters, mobileOS]);

  const loadFeaturedOffers = useCallback(
    async (reset = false, receivedOffers: Offer[]) => {
      if (!reset) return;

      setIsFeaturedOfferLoading(true);
      const offset = 0;
      const limit = 6;
      const limitMobile = 1;
      const filters = reset ? activeFilters : activeFilters;

      try {
        const newFeaturedOffers = await getFeaturedOffers(
          configuration!.hashed_publisher_app_uid!,
          mobileOS,
          offset,
          limit,
          filters,
        );

        let finalFeatured = newFeaturedOffers;
        const detectOSLimit = mobileOS === "" ? limit : limitMobile;

        if (newFeaturedOffers.length === 0) {
          const remainingSlots = detectOSLimit - newFeaturedOffers.length;

          const availableOffers = receivedOffers.filter(
            (offer) => !newFeaturedOffers.some((f: Offer) => f.id === offer.id),
          );

          finalFeatured = [...newFeaturedOffers, ...availableOffers.slice(0, remainingSlots)];
        }

        setOffers((prevOffers) =>
          prevOffers.filter((offer) => !finalFeatured.some((f: Offer) => f.id === offer.id)),
        );

        if (reset) {
          setFeaturedOffers(finalFeatured);
        } else {
          setFeaturedOffers((prevFeaturedOffers: any) => [...prevFeaturedOffers, ...finalFeatured]);
        }
      } catch (error) {
        handleError(error as AxiosError);
      } finally {
        setIsFeaturedOfferLoading(false);
      }
    },
    [isFeaturedOfferLoading, activeFilters, mobileOS],
  );

  const loadOffers = useCallback(
    async (reset = false) => {
      if (!reset && !hasMore) return;

      setIsOfferLoading(true);
      const currentOffset = reset ? 0 : offset;
      const filters = reset ? activeFilters : activeFilters;

      try {
        const newOffers = await getOffers(
          configuration!.hashed_publisher_app_uid!,
          mobileOS,
          currentOffset,
          LIMIT,
          filters,
        );

        const eventOffers = newOffers.map((offer: Offer) => ({
          offerId: offer.id,
          offerTitle: offer.title,
        }));

        if (reset) {
          setOffers(newOffers);
          loadFeaturedOffers(true, newOffers);
        } else {
          setOffers((prevOffers: any) => [...prevOffers, ...newOffers]);
        }

        if (configuration!.offersLimit !== null) {
          setHasMore(false);
        } else if (newOffers.length < LIMIT) {
          setHasMore(false);
        }
        if (configuration!.offersLimit === null) {
          setOffset((prevOffset) => (reset ? LIMIT : prevOffset + LIMIT));
        }
      } catch (error) {
        handleError(error as AxiosError);
      } finally {
        setIsOfferLoading(false);
      }
    },
    [offset, hasMore, isOfferLoading, activeFilters, mobileOS],
  );

  const resetOffsetLoadOffers = async () => {
    setOffers([]);
    setHasMore(true);
    setOffset(0);
    await loadOffers(true);
  };

  const handleScroll = useCallback(() => {
    if (activeTabRef.current !== "all") return;

    const scrollTop = document.documentElement.scrollTop || document.body.scrollTop;
    const scrollHeight = document.documentElement.scrollHeight || document.body.scrollHeight;
    const clientHeight = document.documentElement.clientHeight || window.innerHeight;

    if (
      scrollHeight - scrollTop <= clientHeight + 100 &&
      !isOfferLoading &&
      hasMore &&
      !isFetchingRef.current
    ) {
      isFetchingRef.current = true;

      loadOffers().finally(() => {
        isFetchingRef.current = false;
      });
    }
  }, [isOfferLoading, hasMore, loadOffers]);

  const setFilters = (filters: string[]) => setActiveFilters(filters);

  useEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);

  useEffect(() => {
    if (!isInit) {
      const resetOffersOnFilterChange = async () => {
        setOffers([]);
        setHasMore(true);
        setOffset(0);

        await loadOffers(true);
      };

      resetOffersOnFilterChange();
    }
    setIsInit(false);
  }, [activeFilters]);

  useEffect(() => {
    loadMyOffers();
    loadBannerOffers();
    setActiveFilters(configuration!.offerDesktopDeviceTypesFilter);
    setShowOfferOnboardingWidget(
      configuration!.is_first_offer_attempted && !configuration!.is_offer_onboarding_widget_closed,
    );
  }, []);

  useEffect(() => {
    if (isOfferLoading || isFeaturedOfferLoading || isBannerOfferLoading) {
      document.body.classList.add(c["no-scroll"]);
    } else {
      document.body.classList.remove(c["no-scroll"]);
    }
  }, [isOfferLoading, isFeaturedOfferLoading, isBannerOfferLoading]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isOfferLoading, activeTab, hasMore, offset]);

  const handleScrollThreshold = (treshold: boolean) => {
    setIsThresholdReached(treshold);
  };

  const closeOfferOnboardingWidget = () => {
    setShowOfferOnboardingWidget(false);

    setPanelistState(configuration!.hashed_publisher_app_uid ?? "", {
      isOfferOnboardingWidgetClosed: true,
    })
      .then(({ isFirstOfferAttempted, isOfferOnboardingWidgetClosed }: PanelistState) => {
        configuration!.is_first_offer_attempted = isFirstOfferAttempted;
        configuration!.is_offer_onboarding_widget_closed = isOfferOnboardingWidgetClosed;

        setCacheConfiguration(configuration!);
      })
      .catch((error) => {
        console.error("Error updateing panelist state:", error);
        handleError(error);
      });
  };

  const handleOfferAttempt = () => {
    if (configuration!.is_first_offer_attempted) {
      return;
    }

    getPanelistConfiguration(
      configuration?.hashed_publisher_app_uid ?? "",
      configuration?.publisher_app_uid ?? "",
      configuration?.device_id ?? "",
      configuration?.session_uid ?? "",
      configuration?.language,
    )
      .then(({ isFirstOfferAttempted, isOfferOnboardingWidgetClosed }: PanelistConfig) => {
        configuration!.is_first_offer_attempted = isFirstOfferAttempted;
        configuration!.is_offer_onboarding_widget_closed = isOfferOnboardingWidgetClosed;

        setCacheConfiguration(configuration!);
        setShowOfferOnboardingWidget(
          configuration!.is_first_offer_attempted &&
            !configuration!.is_offer_onboarding_widget_closed,
        );
      })
      .catch((error) => {
        console.error("Error fetching user configuration:", error);
        handleError(error);
      });
  };

  useEffect(() => {
    if (!bannerOffers || bannerOffers.length === 0) {
      setBannerOffer(null);
      return;
    }

    const next = pickNextBannerOfferWithBanner(bannerOffers);
    setBannerOffer(next);
  }, [bannerOffers]);

  function pickNextBannerOfferWithBanner(bannerOffers: Offer[]): Offer | null {
    const withBanner = bannerOffers.filter((o) => !!o.bannerAd);

    if (withBanner.length === 0) return null;

    // If any have featuredRank, rotate by rank; otherwise rotate by array order.
    const hasRank = withBanner.some((o) => typeof o.featuredRank === "number");

    const ordered = hasRank
      ? [...withBanner].sort((a, b) => (a.featuredRank ?? 999999) - (b.featuredRank ?? 999999))
      : withBanner;

    const last = getFeaturedBannerSelection();
    let next = ordered[0];

    if (last) {
      const byId = ordered.findIndex((o) => o.id === last.id);
      if (byId >= 0) {
        next = ordered[(byId + 1) % ordered.length];
      } else if (hasRank && typeof last.featuredRank === "number") {
        next = ordered.find((o) => (o.featuredRank ?? 999999) > last.featuredRank!) ?? ordered[0];
      }
    }

    setFeaturedBannerSelection({
      id: next.id,
      featuredRank: typeof next.featuredRank === "number" ? next.featuredRank : undefined,
    });

    return next;
  }

  return (
    <>
      {(isOfferLoading || isFeaturedOfferLoading || isBannerOfferLoading) && (
        <div className={c["spinner-overlay"]}>
          <Spinner size="normal" />
        </div>
      )}
      {(!isOfferLoading || !isFeaturedOfferLoading) && showOfferOnboardingWidget && (
        <div className={c["offers-alert-container"]}>
          <Alert severity="info" dismissible={true} onClose={closeOfferOnboardingWidget}>
            Great job! You started your first offer. Offer rewards may not be instant, if you
            completed the offer it may take up to 7 days before you are credited.
          </Alert>
        </div>
      )}
      <div className={c["offers-page"]}>
        <div className={c["offers-page-content-wrapper"]}>
          <div className={c["offer-top-bar-wrapper"]}>
            {bannerOffer?.bannerAd && (
              <div className={c["offers-banner-ad-container"]}>
                <div className={c["banner-ad-wrapper"]}>
                  <div className={c["banner-ad-left"]}>
                    <span className={c["banner-head"]}>{bannerOffer.bannerAd.header}</span>
                    <span className={c["banner-text"]}>{bannerOffer.bannerAd.description}</span>
                  </div>

                  <div className={c["banner-ad-right"]}>
                    <button
                      className={c["banner-ad-button"]}
                      type="button"
                      onClick={() => {
                        openModal(bannerOffer, "offer");
                        setBannerOffer(pickNextBannerOfferWithBanner(bannerOffers));
                      }}
                    >
                      {bannerOffer.bannerAd.buttonText}
                    </button>
                  </div>
                </div>
              </div>
            )}
            <div className={c["offers-filter-booster-wrapper"]}>
              {mobileOS === "" && activeFilters && activeFilters.length > 0 && (
                <OfferFilter
                  onDeviceSelection={setFilters}
                  onStartSpinner={() => setIsOfferLoading(true)}
                  disabled={activeTab !== "all"}
                />
              )}
              <div
                className={`${activeFilters && activeFilters.length > 0 ? "" : c["booster-right"]}`}
              >
                <Booster />
              </div>
            </div>
          </div>
          {mobileOS === "" && !isUpdatingOffers && activeFilters && activeFilters.length == 0 && (
            <div className={c["offers-device-selector-container"]}>
              <OfferDeviceSelector
                onDeviceSelection={setFilters}
                setIsUpdatingOffers={setIsUpdatingOffers}
                onStartSpinner={() => setIsOfferLoading(true)}
              />
            </div>
          )}
          <div className={c["offers-tabs-wrapper"]}>
            <div className={c["offers-tabs"]}>
              <button
                className={`${c["tab-button"]} ${activeTab === "all" ? c["active"] : ""}`}
                onClick={() => {
                  handleTabChange("all");
                  if (activeTab !== "all") {
                    resetOffsetLoadOffers();
                    loadMyOffers();
                  }
                }}
              >
                Available Offers
              </button>
              <button
                className={`${c["tab-button"]} ${activeTab === "my-offers" ? c["active"] : ""}`}
                onClick={() => {
                  handleTabChange("my-offers");
                  if (activeTab !== "my-offers") {
                    loadMyOffers();
                  }
                }}
              >
                My Offers
              </button>
            </div>
          </div>
          {activeTab === "all" && (
            <div className={c["offers-container"]}>
              {featuredOffers && featuredOffers.length > 0 && (
                <>
                  <span className={c["offers-section"]}>Top Offers</span>
                  <div className={c["offers-wrapper"]}>
                    {featuredOffers.map((offer: Offer, index: number) => (
                      <HeroOfferCardItem
                        key={offer.id}
                        onModalOpen={() => openModal(offer, "offer")}
                        offer={offer}
                        currencyName={configuration!.currency_name!}
                        index={index}
                        isMostPopular={true}
                        detectOs={mobileOS === ""}
                      />
                    ))}
                  </div>
                </>
              )}

              {/* carousel */}
              {!isCarouselEmpty && (
                <>
                  <div className={c["carousel-divider"]}>
                    {mobileOS !== "" ? (
                      <span className={c["offers-section"]}>Continue Progress and Earn More</span>
                    ) : (
                      <button
                        className={c["desktop-carousel"]}
                        type="button"
                        onClick={() => {
                          handleTabChange("my-offers");
                          loadMyOffers();
                        }}
                      >
                        View All in-Progress Offers
                      </button>
                    )}
                  </div>
                  <OfferCarousel
                    openModal={openModal}
                    detectOs={mobileOS === ""}
                    myOffers={myOffers}
                  />
                </>
              )}
              <span
                className={`${c["offers-section"]} ${
                  isCarouselEmpty && featuredOffers.length > 0 && c["no-carousel"]
                }`}
              >
                All Offers
              </span>
              <div className={`${c["offers-wrapper"]} ${offers.length == 0 ? c["no-offers"] : ""}`}>
                {offers && offers.length > 0
                  ? offers.map((offer: Offer, index: number) => (
                      <HeroOfferCardItem
                        key={offer.id}
                        onModalOpen={() => openModal(offer, "offer")}
                        offer={offer}
                        currencyName={configuration!.currency_name!}
                        index={index}
                        isMostPopular={false}
                        detectOs={mobileOS === ""}
                      />
                    ))
                  : !isUpdatingOffers &&
                    (!isOfferLoading || !isFeaturedOfferLoading) && (
                      <p className={c["no-offers-message"]}>No Offers Available</p>
                    )}
              </div>
            </div>
          )}
          {activeTab === "my-offers" && (
            <div className={c["my-offers-container"]}>
              {myOffers.length === 0 && showEmptyMyOffers && (
                <div className={c["empty-my-offers-container"]}>
                  <div className={c["empty-my-offers-icon-wrapper"]}>
                    <MdWindow />
                  </div>
                  <div className={c["empty-my-offers-text-wrapper"]}>
                    <span className={c["main-text"]}>All in-progress offers will show here!</span>
                    <span className={c["sub-text"]}>Start an offer in the “All Offers” tab!</span>
                  </div>
                </div>
              )}
              {myOffers.length != 0 && (
                <div className={c["filled-my-offers-container"]}>
                  <span className={c["my-offers-section"]}>In-Progress Offers</span>
                  <div className={c["my-offers-wrapper"]}>
                    {myOffers
                      .filter((offer: any) => !offer.isCompleted)
                      .map((offer: any) => (
                        <MyOfferCardItem
                          key={offer.id}
                          onModalOpen={() => openModal(offer, "reward")}
                          offer={offer}
                          currencyName={configuration!.currency_name!}
                          isInCarousel={false}
                        />
                      ))}
                  </div>

                  <span className={c["my-offers-section"]}>Completed Offers</span>
                  <div className={c["my-offers-wrapper"]}>
                    {myOffers.filter((offer: any) => offer.isCompleted).length > 0 ? (
                      myOffers
                        .filter((offer: any) => offer.isCompleted)
                        .map((offer: any) => (
                          <MyOfferCardItem
                            key={offer.id}
                            onModalOpen={() => openModal(offer, "reward")}
                            offer={offer}
                            currencyName={configuration!.currency_name!}
                            isInCarousel={false}
                          />
                        ))
                    ) : (
                      <span className={c["no-completed-offer"]}>
                        Complete all goals to see completed offers here!
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        <MainFooter style={{ marginTop: 0 }} />
      </div>
      <Modal
        show={modalOpen}
        onClose={closeModal}
        treshold={isThresholdReached}
        heroImage={modalContent.heroImageUrl}
      >
        {modalType === "offer" && (
          <HeroOfferModalContent
            modalContent={modalContent}
            onOpenQrModal={openQrModal}
            onOfferAttempt={handleOfferAttempt}
            onScrollTreshold={handleScrollThreshold}
          />
        )}
        {modalType === "reward" && (
          <RewardModalContent
            modalContent={modalContent}
            onOpenQrModal={openQrModal}
            onScrollTreshold={handleScrollThreshold}
          />
        )}
      </Modal>
      <QrModal
        show={qrModalOpen}
        qrCode={qrCode}
        offerUrl={offerUrl}
        operatingSystems={modalContent.operatingSystems}
        onClose={closeQrModal}
      />
    </>
  );
}

export default OffersPage;
